import * as vp from 'vopay';
import { Box, Table, Icon, Checkbox, Pagination } from 'vopay';

import { FunctionComponent, useState, useEffect, useRef, JSX } from 'react';
import styles from './AccountingSystemOpen.module.scss';
import { createPortal } from 'react-dom';
import { useAccountingSoftware } from '../AccountingSoftwareContext';
import BankingUpdateDialogue from './BankingUpdateDialogue/BankingUpdateDialogue';
import BulkPaymentDialogue from './BulkPaymentDialogue/BulkPaymentDialogue';
import AccountingSystemDialogue from './AccountingSystemDialogue/AccountingSystemDialogue';
import AccountingSystemSingleDialogue from './AccountingSystemSingleDialogue/AccountingSystemSingleDialogue';
import AccountingSystemScheduleDialogue from './AccountingSystemScheduleDialogue/AccountingSystemScheduleDialogue';
import AccountingSystemContext, { AccountingSystemCtx } from './AccountingSystemDialogue/AccountingSystemContext';
import NestedTransactionsTable from '../NestedTransactionsTable/NestedTransactionsTable';
import { AccountingSystemDialogueHandler, DialogueHandlerProps } from './AccountingSystemDialogue/types';
import usePayAspInvoice from '../hooks/usePayAspInvoice';
import useCancelAspInvoice from '../hooks/useCancelAspInvoice';
import usePayAspBill from '../hooks/usePayAspBill';
import useCancelAspBill from '../hooks/useCancelAspBill';
import { getAspRowExtras, buildCustomerFromPaymentMethods, AspRowExtras } from '../aspAccountingAdapter';
import ContactNameLink from '../ContactNameLink';
import useFetchContactPaymentMethods from '../hooks/useFetchContactPaymentMethods';
import useFetchFinancialInstitutions from '../hooks/useFetchFinancialInstitutions';
import { getAvailablePaymentMethodsDropdown, IS_SCHEDULING_SUPPORTED_ON_ASP } from './utils';
import { format, isAfter, parseISO, startOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useToast } from '#shared/providers/Toast';
import { useBlockingLoader } from '#shared/providers/Loader';

import { Button, TransactionStatus } from '#shared/components/UI';

type AccountingSystemOpenProps = {
  BillsOrInvoices: vp.BillOrInvoiceRailz[];

  onSuccess: () => void;
  ServiceName: string;
  AccountBalance?: vp.AccountBalanceResponse;
  Type: 'Payable' | 'Receivable';
  AspConnectionID?: number;
  // Hidden when the list is scoped to one contact's own view.
  showContactColumn?: boolean;
  // Opens the contact detail drawer for a row's linked record.
  onContactClick?: (clientAccountId: string) => void;
  onActionsReady?: (actions: { onScheduleAll: () => void }) => void;
};
enum ToastVisibilityState {
  ReceivableWithPaymentMethods = 'ReceivableWithPaymentMethods',
  ScheduledForCollection = 'ScheduledForCollection',
  PaymentMethodSaved = 'PaymentMethodSaved',
  CollectionRequestSent = 'CollectionRequestSent',
  CollectionScheduled = 'CollectionScheduled',
  MultipleRecipientsRequestSent = 'MultipleRecipientsRequestSent',
  MultipleRecipientsScheduled = 'MultipleRecipientsScheduled',
  PaymentMethodSavedToRecipients = 'PaymentMethodSavedToRecipients',
  ScheduledForDate = 'ScheduledForDate',
  PartialCollection = 'PartialCollection',
  FinalCollectionRequestSent = 'FinalCollectionRequestSent',
  ScheduleDateUpdate = 'ScheduleDateUpdate',
}

type FC = FunctionComponent<AccountingSystemOpenProps>;

const AccountingSystemOpen: FC = ({
  BillsOrInvoices,
  Type,
  ServiceName,
  AccountBalance,
  AspConnectionID,
  showContactColumn = true,
  onContactClick,
  onSuccess,
  onActionsReady,
}: AccountingSystemOpenProps) => {
  const { account: response, userPermissions, hostActions } = useAccountingSoftware();
  const actionsEnabled = userPermissions.payAndCollect;
  const [financialInstitutionsResp, fetchFinancialInstitutions] = useFetchFinancialInstitutions({});
  const [allCheckbox, setAllCheckbox] = useState(false);
  const [tempBillsOrInvoices, setTempBillsOrInvoices] = useState<vp.TemporaryBillsOrInvoicesRails[]>([]);
  const [selectedBillsOrInvoices, setSelectedBillsOrInvoices] = useState<vp.TemporaryBillsOrInvoicesRails[]>([]);
  const [confirmedBillsOrInvoices, setConfirmedBillsOrInvoices] = useState<vp.TemporaryBillsOrInvoicesRails[]>([]);
  const [confirmedDate, setConfirmedDate] = useState('');
  const [isReceivableWithPaymentMethodsVisible, setIsReceivableWithPaymentMethodsVisible] = useState(false);
  const [isScheduledForCollectionVisible, setIsScheduledForCollectionVisible] = useState(false);
  const [isPaymentMethodSavedVisible, setIsPaymentMethodSavedVisible] = useState(false);
  const [isCollectionRequestSentVisible, setIsCollectionRequestSentVisible] = useState(false);
  const [isCollectionScheduledVisible, setIsCollectionScheduledVisible] = useState(false);
  const [isMultipleRecipientsRequestSentVisible, setIsMultipleRecipientsRequestSentVisible] = useState(false);
  const [isMultipleRecipientsScheduledVisible, setIsMultipleRecipientsScheduledVisible] = useState(false);
  const [isPaymentMethodSavedToRecipientsVisible, setIsPaymentMethodSavedToRecipientsVisible] = useState(false);

  const [isPartialCollectionVisible, setIsPartialCollectionVisible] = useState(false);
  const [isFinalCollectionRequestSentVisible, setIsFinalCollectionRequestSentVisible] = useState(false);
  const [showSuccessScheduleDateUpdate, setShowSuccessScheduleDateUpdated] = useState(false);
  const [showSuccessScheduleCanceled, setShowSuccessScheduleCanceled] = useState(false);

  const [showInsufficientFunds, setShowInsufficientFunds] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [clientName, setClientName] = useState('');
  const [showBulkPayment, setShowBulkPayment] = useState(false);

  const [isSingleTransaction, setIsSingleTransaction] = useState(false);

  const [updatingItem, setUpdatingItem] = useState<vp.TemporaryBillsOrInvoicesRails>();
  const blockingLoader = useBlockingLoader();
  const { payAspInvoice } = usePayAspInvoice();
  const { cancelAspInvoice } = useCancelAspInvoice();
  const { payAspBill } = usePayAspBill();
  const { cancelAspBill } = useCancelAspBill();
  const { fetchContactPaymentMethods } = useFetchContactPaymentMethods();
  const [showSingleCollect, setShowSingleCollect] = useState(false);
  const [showNoPaymentMethod, setShowNoPaymentMethod] = useState(false);
  const [showCancelSchedule, setShowCancelSchedule] = useState(false);
  const [showEditSchedule, setShowEditSchedule] = useState(false);

  const [isUpdateInfo, setUpdateInfo] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(String(Date.now()));

  const [isEditTransaction, setIsEditTransaction] = useState(false);
  const [showScheduleAll, setShowScheduleAll] = useState(false);
  const [hasItemsWithNoPaymentMethod, setHasItemsWithNoPaymentMethod] = useState(false);
  const [showScheduleCustomDate, setShowScheduleCustomDate] = useState(false);
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AccountingSystem.OpenTab' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Shared' });
  const { t: tRoot } = useTranslation();
  const toast = useToast();

  const [currentPage, setCurrentPage] = useState(0);
  const PER_PAGE = 20;
  const offset = currentPage * PER_PAGE;
  const pageCount = Math.ceil(tempBillsOrInvoices.length / PER_PAGE);

  useEffect(() => {
    if (showSuccessScheduleCanceled) {
      setShowSuccessScheduleCanceled(false);
    }
  }, [showSuccessScheduleCanceled]);

  useEffect(() => {
    if (showSuccessScheduleDateUpdate) {
      setShowSuccessScheduleDateUpdated(false);
    }
  }, [showSuccessScheduleDateUpdate]);

  useEffect(() => {
    if (isReceivableWithPaymentMethodsVisible) {
      setIsReceivableWithPaymentMethodsVisible(false);
    }
  }, [isReceivableWithPaymentMethodsVisible]);

  useEffect(() => {
    if (isScheduledForCollectionVisible) {
      setIsScheduledForCollectionVisible(false);
    }
  }, [isScheduledForCollectionVisible]);

  useEffect(() => {
    if (isPaymentMethodSavedVisible) {
      setIsPaymentMethodSavedVisible(false);
    }
  }, [isPaymentMethodSavedVisible]);

  useEffect(() => {
    if (isCollectionRequestSentVisible) {
      setIsCollectionRequestSentVisible(false);
    }
  }, [isCollectionRequestSentVisible]);

  useEffect(() => {
    if (isCollectionScheduledVisible) {
      setIsCollectionScheduledVisible(false);
    }
  }, [isCollectionScheduledVisible]);

  useEffect(() => {
    if (isMultipleRecipientsRequestSentVisible) {
      setIsMultipleRecipientsRequestSentVisible(false);
    }
  }, [isMultipleRecipientsRequestSentVisible]);

  useEffect(() => {
    if (isMultipleRecipientsScheduledVisible) {
      setIsMultipleRecipientsScheduledVisible(false);
    }
  }, [isMultipleRecipientsScheduledVisible]);

  useEffect(() => {
    if (isPaymentMethodSavedToRecipientsVisible) {
      setIsPaymentMethodSavedToRecipientsVisible(false);
    }
  }, [isPaymentMethodSavedToRecipientsVisible]);

  useEffect(() => {
    if (isPartialCollectionVisible) {
      setIsPartialCollectionVisible(false);
    }
  }, [isPartialCollectionVisible]);

  useEffect(() => {
    if (isFinalCollectionRequestSentVisible) {
      setIsFinalCollectionRequestSentVisible(false);
    }
  }, [isFinalCollectionRequestSentVisible]);

  // The date pickers store ms-timestamp strings; the asp endpoint expects Y-m-d.
  const toApiDate = (value?: string) => {
    if (!value || value === '0000-00-00') return undefined;

    return /^\d+$/.test(value) ? format(new Date(Number(value)), 'yyyy-MM-dd') : value;
  };

  const onPayOrCollect = async (selectedList: vp.TemporaryBillsOrInvoicesRails[], state: ToastVisibilityState) => {
    // Both sides go through the flat asp pay endpoints — one call per document;
    // money movement resolves server-side from the contact's client-account
    // payment method for the chosen rail.
    if (!AspConnectionID) return;

    // Payables draw down the account balance, so keep the legacy guard.
    if (Type === 'Payable' && !checkBalance(selectedList)) return;

    blockingLoader.show();

    const payable = selectedList
      .map((row) => ({ row, extras: getAspRowExtras(row) }))
      // No saved payment method resolves no rail (getPaymentRailsAccountingSoftware
      // falls back to 'eft' for an empty method, so PaymentRail is never blank)
      // — the schedule-all dialog already warns these rows are skipped.
      .filter(
        (item): item is { row: vp.TemporaryBillsOrInvoicesRails; extras: AspRowExtras } =>
          !!item.extras && !!item.row.paymentMethodUser,
      );

    const results = await Promise.allSettled(
      payable.map(({ row, extras }) => {
        const payment = {
          ConnectionID: AspConnectionID,
          PaidAmount: Number(row.PaidAmount),
          PaymentRail: row.PaymentRail,
          // While scheduling is hidden, never send a date — rows can still
          // carry a future scheduledDate (bulk flows default it to the due
          // date), and the backend processes immediately regardless.
          ScheduledDate: IS_SCHEDULING_SUPPORTED_ON_ASP ? toApiDate(row.scheduledDate) : undefined,
          CreditNotes: row.creditNotes || undefined,
        };

        return Type === 'Receivable'
          ? payAspInvoice({ ...payment, InvoiceID: extras.AspDocumentID })
          : payAspBill({ ...payment, BillID: extras.AspDocumentID });
      }),
    );

    blockingLoader.hide();

    const rejected = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');

    if (rejected.length) {
      const reason = rejected[0].reason;
      const msg = reason instanceof Error ? reason.message : tGlobal('errorOccurred');

      setShowError(true);

      setErrorMessage(
        t('bulkPaymentError', {
          defaultValue:
            '{{numFailed}} of {{numTotal}} payments failed: {{msg}}. The rest went through — refresh the list before retrying so you do not pay them twice.',
          numFailed: rejected.length,
          numTotal: payable.length,
          msg,
        }),
      );

      // Refresh either way so whatever succeeded drops off the open list.
      onSuccess();

      return;
    }

    if (isEditTransaction) {
      setShowSuccessScheduleDateUpdated(true);
      setIsEditTransaction(false);
    } else if (isSingleTransaction) {
      setIsSingleTransaction(false);
    }

    onSuccess();
    enableToastVisibility(state);
  };

  const collectOrPayAllOnDueDate = () => {
    const processedList = formatDateOfObjects(tempBillsOrInvoices);
    onPayOrCollect(processedList, ToastVisibilityState.ScheduledForCollection);
  };

  const collectOrPayBulkSchedule = (selectedList: vp.TemporaryBillsOrInvoicesRails[], state: ToastVisibilityState) => {
    const processedList = formatDateOfObjects(selectedList);
    onPayOrCollect(processedList, state);
  };

  const updateScheduleDate = (
    newDate: string,
    selectedList: vp.TemporaryBillsOrInvoicesRails[],
    state: ToastVisibilityState,
  ) => {
    const processedList = selectedList.map((billOrInvoice) => ({
      ...billOrInvoice,
      scheduledDate: newDate,
    }));

    onPayOrCollect(processedList, state);
  };

  const accountingSystemCtx: AccountingSystemCtx = {
    showInsufficientFunds,
    showError,
    errorMessage,
    clientName,
    showCancel,
    type: Type,
    showNoPaymentMethod,
    showCancelSchedule,
    hasItemsWithNoPaymentMethod,
    showScheduleAll,
    showScheduleCustomDate,
  };

  const handlers = {
    handleAccountDialogue: (handlerProps: DialogueHandlerProps<AccountingSystemDialogueHandler>) => {
      const { type: typeHandler, state: state2, action = 'default' } = handlerProps;

      const dialogueHandlers: AccountingSystemDialogueHandler = {
        scheduleCustomDate: {
          confirm: {
            default: () => {
              updateScheduleDate(confirmedDate, confirmedBillsOrInvoices, ToastVisibilityState.ScheduleDateUpdate);
              setShowScheduleCustomDate(false);
            },
          },
          fail: {
            default: () => {
              setShowScheduleCustomDate(false);
              setShowBulkPayment(true);
            },
          },
        },
        cancelSchedule: {
          confirm: {
            default: () => {
              setShowCancelSchedule(false);

              const extras = updatingItem && getAspRowExtras(updatingItem);

              if (extras) {
                const cancelRequest =
                  Type === 'Receivable'
                    ? cancelAspInvoice({ InvoiceID: extras.AspDocumentID })
                    : cancelAspBill({ BillID: extras.AspDocumentID });

                cancelRequest
                  .then(() => {
                    setShowSuccessScheduleCanceled(true);
                    onSuccess();
                  })
                  .catch((error) => {
                    setErrorMessage(error instanceof Error ? error.message : '');
                    setShowError(true);
                  });
              }
            },
          },
          fail: {
            default: () => {
              setShowCancelSchedule(false);
            },
          },
        },
        noPaymentMethod: {
          confirm: {
            default: () => {
              setShowNoPaymentMethod(false);
            },
          },
          close: {
            default: () => {
              setShowNoPaymentMethod(false);
            },
          },
        },
        error: {
          confirm: {
            default: () => {
              setShowError(false);
            },
          },
        },
        cancel: {
          confirm: {
            default: () => {
              setShowCancel(false);
            },
          },
          fail: {
            default: () => {
              setShowCancel(false);
              setShowBulkPayment(true);
            },
          },
        },
        insufficient: {
          confirm: {
            default: () => {
              hostActions.onFundAccount?.();
              setShowInsufficientFunds(false);
            },
          },
          fail: {
            default: () => {
              setShowInsufficientFunds(false);
            },
          },
        },
        scheduleAll: {
          confirm: {
            default: () => {
              collectOrPayAllOnDueDate();
            },
          },
          fail: {
            default: () => {
              setShowScheduleAll(false);
            },
          },
        },

        singleCollect: {
          confirm: {
            default: () => {},
          },
          fail: {
            default: () => {},
          },
        },
      };

      const dialogueState = dialogueHandlers[typeHandler][state2];
      const handler = dialogueState ? dialogueState[action] || dialogueState.default : () => {};

      handler();
    },
  };

  useEffect(() => {
    // Only the pay/collect dialogs read these. Keyed on actionsEnabled, not
    // mount: hosts push permissions over SET_CONFIG after the page is up.
    if (!actionsEnabled) return;

    fetchFinancialInstitutions({
      Country: response.Country,
    });
  }, [actionsEnabled]);

  // One row's load-time mapping: availability flags + the temp Customer shape.
  // Also reused when a row is enriched with lazily fetched payment methods.
  const buildTempRow = (billOrInvoice: vp.BillOrInvoiceRailz): vp.TemporaryBillsOrInvoicesRails => {
    const hasDetails = {
      hasBankAccount: !!(
        billOrInvoice.Customer &&
        billOrInvoice.Customer.BankAccount &&
        billOrInvoice.Customer.BankAccount.AccountNumber &&
        ((response.Country === 'CA' &&
          ((Type === 'Receivable' && response.EFTCollectEnabled) ||
            (Type === 'Payable' && (response.EFTSendEnabled || response.RTRWithdrawEnabled)))) ||
          (response.Country === 'US' &&
            ((Type === 'Receivable' && response.ACHFundEnabled) ||
              (Type === 'Payable' && (response.ACHWithdrawEnabled || response.RTPWithdrawEnabled)))))
      ),
      hasInterac: !!(
        (
          billOrInvoice.Customer &&
          billOrInvoice.Customer.Interac &&
          (billOrInvoice.Customer.Interac.RecipientEmailAddress ||
            billOrInvoice.Customer.Interac?.RecipientPhoneNumber) &&
          (billOrInvoice.Customer.Interac.Question || Type === 'Receivable') &&
          (billOrInvoice.Customer.Interac.Answer || Type === 'Receivable') &&
          ((response.Country === 'CA' && Type === 'Receivable' && response.InteracMoneyRequestEnabled) ||
            (response.Country === 'CA' && Type === 'Payable' && response.InteracBulkPayoutEnabled))
        )
        // country canada money request Receivable, payout, payable
      ),
      hasDebitCard: !!(
        billOrInvoice.Customer &&
        billOrInvoice.Customer.DebitCard &&
        billOrInvoice.Customer.DebitCard.PushToCardToken &&
        response.PushToCardEnabled &&
        Type === 'Payable'
      ),
      hasCreditCard: !!(
        billOrInvoice.Customer &&
        billOrInvoice.Customer.CreditCard &&
        billOrInvoice.Customer.CreditCard.CreditCardToken &&
        response.CreditCardEnabled &&
        Type === 'Receivable'
      ),
    };

    return {
      ...billOrInvoice,
      scheduledDate: billOrInvoice.ScheduledDate,
      Customer: {
        ...(billOrInvoice.Customer && {
          ...billOrInvoice.Customer,
          BankAccount: {
            RoutingNumber: billOrInvoice.Customer.BankAccount?.ABARoutingNumber,
            AccountNumber: billOrInvoice.Customer.BankAccount?.AccountNumber || '',
            BranchTransitNumber: billOrInvoice.Customer.BankAccount?.BranchTransitNumber,
            FinancialInstitutionNumber: billOrInvoice.Customer.BankAccount?.FinancialInstitutionNumber,
            PhoneNumber: billOrInvoice.Customer.BankAccount?.Phone || '',
            City: billOrInvoice.Customer.BankAccount?.City || '',
            Address: billOrInvoice.Customer.BankAccount?.Address || '',
            State: billOrInvoice.Customer.BankAccount?.State || '',
            ZipCode: billOrInvoice.Customer.BankAccount?.PostalCode || '',
            ACTCurrencies_ID: response.Country === 'CA' ? 2 : 1,
          },
          Interac: {
            EmailAddress: billOrInvoice.Customer.Interac?.RecipientEmailAddress,
            PhoneNumber: billOrInvoice.Customer.Interac?.RecipientPhoneNumber,
            Question: billOrInvoice.Customer.Interac?.Question,
            Answer: billOrInvoice.Customer.Interac?.Answer,
            Language: billOrInvoice.Customer.Interac?.Language || 'en',
          },
          CreditCard: {
            ...billOrInvoice.Customer.CreditCard,
            Token: billOrInvoice.Customer.CreditCard?.CreditCardToken,
          },
          DebitCard: {
            ...billOrInvoice.Customer.DebitCard,
            Token: billOrInvoice.Customer.DebitCard?.PushToCardToken,
          },
        }),
      },
      PaymentRail: vp.getPaymentRailsAccountingSoftware(
        Object.values(vp.getAvailablePaymentMethodsDropdown(hasDetails, response.Country, Type))[0] || '',
        response.Country,
        Type,
      ),
      ...hasDetails,
      isSelected: allCheckbox,
      isExpanded: false,
      PaidAmount: billOrInvoice.amountDue ? billOrInvoice.amountDue : billOrInvoice.totalAmount,
      paymentMethodUser:
        Object.values(vp.getAvailablePaymentMethodsDropdown(hasDetails, response.Country, Type))[0] || '',
    };
  };

  // Payment methods live on each contact's client account and are fetched
  // lazily — per action, not per list load (a fetch per contact at load time
  // didn't scale). Resolved methods are cached per client account and folded
  // back into the row via the same load-time mapping, preserving user edits.
  const methodsCache = useRef(new Map<string, vp.PaymentMethod[]>());

  useEffect(() => {
    // Every list refetch follows an action that may have changed a contact's
    // saved methods (pay, method save) — drop the cache so the next lazy
    // enrichment sees the current state.
    methodsCache.current.clear();

    // The list is re-set more than once around load (the enrichment pass
    // lands after the first render), so a rebuild must not wipe a checkbox the
    // user already ticked — carry the selection over by row id.
    setTempBillsOrInvoices((rows) => {
      const selectedIds = new Set(rows.filter((row) => row.isSelected).map((row) => row.id));

      return BillsOrInvoices.map((billOrInvoice) => {
        const rebuilt = buildTempRow(billOrInvoice);

        return selectedIds.has(rebuilt.id) ? { ...rebuilt, isSelected: true } : rebuilt;
      });
    });
  }, [BillsOrInvoices]);

  // A row whose newest payment failed is flagged (row tint); its action stays Collect/Pay.
  const hasFailedPayment = (row: vp.TemporaryBillsOrInvoicesRails) => !!getAspRowExtras(row)?.AspFailedPayment;

  // The container registers onScheduleAll once (onActionsReady), so that
  // callback would close over the initial empty list — read via a ref instead.
  const tempRowsRef = useRef<vp.TemporaryBillsOrInvoicesRails[]>([]);

  useEffect(() => {
    tempRowsRef.current = tempBillsOrInvoices;
  }, [tempBillsOrInvoices]);

  const enrichTempRows = async (
    targetRows: vp.TemporaryBillsOrInvoicesRails[],
  ): Promise<vp.TemporaryBillsOrInvoicesRails[]> => {
    const clientAccountIds = [
      ...new Set(
        targetRows
          .map((row) => getAspRowExtras(row)?.AspClientAccountID)
          .filter((id): id is string => !!id && !methodsCache.current.has(id)),
      ),
    ];

    if (clientAccountIds.length) {
      blockingLoader.show();

      try {
        await Promise.all(
          clientAccountIds.map(async (id) => {
            try {
              const res = await fetchContactPaymentMethods(id);
              methodsCache.current.set(id, res.PaymentMethods);
            } catch {
              // A contact whose methods fail to load behaves as method-less.
              methodsCache.current.set(id, []);
            }
          }),
        );
      } finally {
        blockingLoader.hide();
      }
    }

    const enriched = targetRows.map((row) => {
      const extras = getAspRowExtras(row);
      const methods = (extras?.AspClientAccountID && methodsCache.current.get(extras.AspClientAccountID)) || [];
      if (!methods.length) return row;

      const rebuilt = buildTempRow({
        ...row,
        // The temp shape renames ScheduledDate → scheduledDate; restore the
        // wire-shape key the mapper reads.
        ScheduledDate: row.scheduledDate || '',
        Customer: buildCustomerFromPaymentMethods(
          row.Customer?.Name || row.customerRef?.name || row.vendorRef?.name || null,
          methods,
        ),
      });

      return {
        ...rebuilt,
        AspPaymentMethods: methods,
        // Keep the user-owned state of the existing row.
        isSelected: row.isSelected,
        isExpanded: row.isExpanded,
        PaidAmount: row.PaidAmount,
        scheduledDate: row.scheduledDate,
        creditNotes: row.creditNotes,
        paymentMethodUser: row.paymentMethodUser || rebuilt.paymentMethodUser,
        PaymentRail: row.paymentMethodUser ? row.PaymentRail : rebuilt.PaymentRail,
      };
    });

    const enrichedById = new Map(enriched.map((row) => [row.id, row]));
    setTempBillsOrInvoices((rows) => rows.map((row) => enrichedById.get(row.id) || row));

    return enriched;
  };

  useEffect(() => {
    setSelectedBillsOrInvoices(tempBillsOrInvoices.filter((billOrInvoice) => billOrInvoice.isSelected));
  }, [tempBillsOrInvoices]);

  const onCheckedInvoice = (id: string) => {
    setTempBillsOrInvoices(
      tempBillsOrInvoices.map((billOrInvoice) => {
        if (billOrInvoice.id === id) {
          return { ...billOrInvoice, isSelected: !billOrInvoice.isSelected };
        }

        return billOrInvoice;
      }),
    );
  };

  const checkToggleInvoices = () => {
    if (!allCheckbox) {
      setTempBillsOrInvoices(
        tempBillsOrInvoices.map((billOrInvoice, index) => ({
          ...billOrInvoice,
          isSelected: index < 20, // Only check the first 20 items
        })),
      );
    } else {
      setTempBillsOrInvoices(
        tempBillsOrInvoices.map((billOrInvoice) => ({
          ...billOrInvoice,
          isSelected: false,
        })),
      );
    }

    setAllCheckbox(!allCheckbox);
  };

  const uncheckAllInvoices = () => {
    setTempBillsOrInvoices(
      tempBillsOrInvoices.map((billOrInvoice) => ({
        ...billOrInvoice,
        isSelected: false,
      })),
    );

    setAllCheckbox(false);
  };

  const onShowScheduleAll = async () => {
    // Resolve every open row's methods so the dialog's "will not be scheduled"
    // warning and the per-row rails are accurate before confirming.
    const enriched = await enrichTempRows(tempRowsRef.current);

    setHasItemsWithNoPaymentMethod(
      enriched.some(
        (billOrInvoice) =>
          !billOrInvoice.hasBankAccount &&
          !billOrInvoice.hasCreditCard &&
          !billOrInvoice.hasInterac &&
          !billOrInvoice.hasDebitCard,
      ),
    );

    setShowScheduleAll(true);
  };

  useEffect(() => {
    if (onActionsReady) {
      onActionsReady({
        onScheduleAll: onShowScheduleAll,
      });
    }
  }, []);

  useEffect(() => {
    if (showSuccessScheduleDateUpdate) {
      toast.success(t('successScheduleDateUpdateToast', { defaultValue: 'Schedule Date Updated' }));
    }
  }, [showSuccessScheduleDateUpdate]);

  useEffect(() => {
    if (showSuccessScheduleCanceled) {
      toast.success(t('successScheduleCanceled', { context: Type }));
    }
  }, [showSuccessScheduleCanceled, Type]);

  useEffect(() => {
    if (isReceivableWithPaymentMethodsVisible) {
      toast.success(t('receivableWithPaymentMethodsVisibleToast', { context: Type }));
    }
  }, [isReceivableWithPaymentMethodsVisible, Type]);

  useEffect(() => {
    if (isScheduledForCollectionVisible) {
      toast.success(t('scheduledForCollectionVisibleToast', { context: Type }));
    }
  }, [isScheduledForCollectionVisible, Type]);

  useEffect(() => {
    if (isPaymentMethodSavedVisible) {
      toast.success(t('paymentMethodSavedVisibleToast', { context: Type }));
    }
  }, [isPaymentMethodSavedVisible, Type]);

  useEffect(() => {
    if (isCollectionRequestSentVisible) {
      toast.success(t('collectionRequestSentVisibleToast', { context: Type }));
    }
  }, [isCollectionRequestSentVisible, Type]);

  useEffect(() => {
    if (isCollectionScheduledVisible) {
      toast.success(t('collectionScheduledVisibleToast', { context: Type }));
    }
  }, [isCollectionScheduledVisible, Type]);

  useEffect(() => {
    if (isMultipleRecipientsRequestSentVisible) {
      toast.success(t('multipleRecipientsRequestSentToast', { context: Type }));
    }
  }, [isMultipleRecipientsRequestSentVisible, Type]);

  useEffect(() => {
    if (isMultipleRecipientsScheduledVisible) {
      toast.success(t('multipleRecipientsScheduledToast', { context: Type }));
    }
  }, [isMultipleRecipientsScheduledVisible, Type]);

  useEffect(() => {
    if (isPaymentMethodSavedToRecipientsVisible) {
      toast.success(t('paymentMethodSavedToRecipientsToast', { context: Type }));
    }
  }, [isPaymentMethodSavedToRecipientsVisible, Type]);

  useEffect(() => {
    if (isPartialCollectionVisible) {
      toast.success(t('partialCollectionToast', { context: Type }));
    }
  }, [isPartialCollectionVisible, Type]);

  useEffect(() => {
    if (isFinalCollectionRequestSentVisible) {
      toast.success(t('finalCollectionRequestSentToast', { context: Type }));
    }
  }, [isFinalCollectionRequestSentVisible, Type]);

  return (
    <AccountingSystemContext.Provider value={accountingSystemCtx}>
      <Box flx="col" width="100%" position="relative">
        {actionsEnabled && selectedBillsOrInvoices.length > 0 && (
          <div className={styles.selectedBar}>
            <Box pt="sm" pb="sm" jc="sb" ai="center">
              <Box gap="none" ai="center">
                <div>
                  {t('itemsSelected', {
                    defaultValue: '{{count}} Item(s) Selected',
                    count: selectedBillsOrInvoices.length,
                  })}
                </div>
              </Box>

              <Box gap="none">
                <Button
                  colorScheme="secondary"
                  size="small"
                  id="bulk-collect"
                  onClick={async () => {
                    // Resolve the selected rows' methods so the bulk dialog's
                    // per-item dropdowns and rails are populated.
                    const enriched = await enrichTempRows(selectedBillsOrInvoices);

                    setConfirmedBillsOrInvoices(enriched);
                    setShowBulkPayment(true);
                  }}
                >
                  {`${Type === 'Receivable' ? t('collectSelectedButton', { defaultValue: 'Collect Selected' }) : t('paySelectedButton', { defaultValue: 'Pay Selected' })}`}
                </Button>

                <Icon height="28px" width="25px" icon="line" />

                <Button
                  colorScheme="secondary"
                  size="small"
                  id="bulk-cancel"
                  onClick={() => {
                    uncheckAllInvoices();
                  }}
                >
                  {tGlobal('Buttons.cancel', { defaultValue: 'Cancel' })}
                </Button>
              </Box>
            </Box>
          </div>
        )}

        <Box>
          <Table
            id="open-bills-invoices"
            version="2"
            zebra
            columns={[
              {
                id: 'checkbox',
                visible: actionsEnabled,
                header: (
                  <Checkbox
                    name="select-all-invoices"
                    value={allCheckbox}
                    onBlur={() => {}}
                    onChange={checkToggleInvoices}
                    size="sm"
                    variant="rounded"
                    hasNoPadding
                  />
                ),
                align: 'left',
                template: ({ data }) => (
                  <Checkbox
                    name={`select-invoice-${data.id || ''}`}
                    value={data.isSelected}
                    onBlur={() => {}}
                    onChange={() => onCheckedInvoice(data.id)}
                    size="sm"
                    variant="rounded"
                    hasNoPadding
                    disabled={tempBillsOrInvoices.filter((i) => i.isSelected).length >= 10 && data.isSelected === false}
                  />
                ),
              },
              {
                id: 'date',
                header:
                  Type === 'Receivable'
                    ? t('Table.dateReceivable', { defaultValue: 'Invoice Date' })
                    : t('Table.date', { defaultValue: 'Bill Date' }),
                align: 'left',
                template: ({ data }) =>
                  data.postedDate ? format(new Date(data.postedDate.replace('Z', '')), 'yyyy-MM-dd') : '',
              },
              {
                id: 'name',
                header:
                  Type === 'Receivable'
                    ? t('Table.nameReceivable', { defaultValue: 'Customer' })
                    : t('Table.name', { defaultValue: 'Supplier' }),
                align: 'left',
                visible: showContactColumn,
                template: ({ data }) => <ContactNameLink row={data} onOpenContact={onContactClick} />,
              },
              {
                id: 'invoiceNumber',
                header:
                  Type === 'Receivable'
                    ? t('Table.invoiceNumberReceivable', { defaultValue: 'Invoice No.' })
                    : t('Table.invoiceNumber', { defaultValue: 'Bill No.' }),
                align: 'left',
                template: ({ data }) => data.invoiceNumber || data.vendorInvoiceNumber || '',
              },
              {
                id: 'amount',
                header: t('Table.amount', { defaultValue: 'Amount' }),
                align: 'left',
                template: ({ data }) => {
                  const isPartiallySettled = Number(data.amountDue) !== Number(data.totalAmount);

                  // Partially settled rows lead with what is still owed; the original
                  // total becomes secondary info (design: "Remaining CAD 1,500.00
                  // / CAD 3,000.00").
                  if (isPartiallySettled) {
                    return (
                      <Box flx="col" gapSize="2px">
                        <span className={styles.remainingLabel}>
                          {t('Table.Row.remainingLabel', { defaultValue: 'Remaining' })}
                        </span>

                        <Box flx="row" ai="baseline" gapSize="4px">
                          <span>{`${data?.currency || ''} ${vp.formattedCurrency(data.amountDue.toString())}`}</span>

                          <span className={styles.totalAmountSecondary}>
                            {`/ ${data?.currency || ''} ${vp.formattedCurrency(data.totalAmount.toString())}`}
                          </span>
                        </Box>
                      </Box>
                    );
                  }

                  return <span>{`${data?.currency || ''} ${vp.formattedCurrency(data.totalAmount.toString())}`}</span>;
                },
              },
              {
                id: 'dueDate',
                header: t('Table.dueDate', { defaultValue: 'Due Date' }),
                align: 'left',
                template: ({ data }) =>
                  data.dueDate ? (
                    <Box flx="row" ai="center" gapSize="8px">
                      <Box flx="col" width="85px">
                        <div>{format(new Date(data.dueDate.replace('Z', '')), 'yyyy-MM-dd')}</div>
                        <DateDisplay date={new Date(data.dueDate)} />
                      </Box>
                    </Box>
                  ) : (
                    ''
                  ),
              },
              {
                id: 'status',
                header: t('Table.status', { defaultValue: 'Status' }),
                align: 'left',
                template: ({ data }) => {
                  if (data.status?.toLowerCase() === 'reversed') {
                    return (
                      <TransactionStatus
                        status="reversed"
                        label={t('Table.Row.statusReversed', { defaultValue: 'Reversed' })}
                      />
                    );
                  }

                  if (data.status === 'partiallyPaid') {
                    // "Only show status in red if the last transaction status failed"
                    // (Figma dev annotation) — the latest payment, not any payment.
                    const payments = data.PaymentDetails || [];
                    const hasFailedLatestPayment = payments[payments.length - 1]?.Status === 'failed';

                    return (
                      <TransactionStatus
                        status={hasFailedLatestPayment ? 'partially paid failed' : 'partially paid'}
                        label={
                          Type === 'Receivable'
                            ? t('Table.Row.statusPartiallyCollected', { defaultValue: 'Partially Collected' })
                            : t('Table.Row.statusPartiallyPaid', { defaultValue: 'Partially Paid' })
                        }
                      />
                    );
                  }

                  return (
                    <TransactionStatus status="open" label={t('Table.Row.statusOpen', { defaultValue: 'Open' })} />
                  );
                },
              },
              {
                id: 'view',
                header: '',
                align: 'right',
                template: ({ data }) =>
                  IS_SCHEDULING_SUPPORTED_ON_ASP && data.scheduledDate && data.scheduledDate !== '0000-00-00' ? (
                    <Box flx="row" ai="center" jc="fe" gapSize="8px">
                      <button
                        type="button"
                        className={styles.scheduleButton}
                        onClick={async () => {
                          // Rescheduling re-pays with the row's rail, which needs the
                          // lazily fetched methods.
                          const [row] = await enrichTempRows([data]);

                          setUpdatingItem(row);

                          setScheduleDate(
                            row.scheduledDate ? parseISO(row.scheduledDate).getTime().toString() : String(Date.now()),
                          );

                          setShowEditSchedule(true);
                        }}
                      >
                        <Icon icon="calendarAccounting" color="none" width="16px" height="auto" />

                        <span>
                          {t('Table.Row.scheduledFor', {
                            defaultValue: 'Scheduled for {{date}}',
                            date: format(parseISO(data.scheduledDate), 'yyyy-MM-dd'),
                          })}
                        </span>
                      </button>

                      <Button
                        colorScheme="secondary"
                        size="smallest"
                        iconLeft="close"
                        id={`scheduled-remove-${data.id || ''}`}
                        onClick={() => {
                          setUpdatingItem(data);
                          setShowCancelSchedule(true);
                        }}
                      />
                    </Box>
                  ) : (
                    <Box jc="fe" gap="sm" ai="center">
                      {actionsEnabled && !data.isSelected && (
                        <Button
                          id={`details-${data.id || ''}`}
                          colorScheme="secondary"
                          size="small"
                          onClick={async () => {
                            // Methods are fetched lazily — resolve this row's before
                            // deciding which dialog fits it.
                            const [row] = await enrichTempRows([data]);

                            setUpdatingItem(row);
                            setClientName(row.Customer?.Name || row?.customerRef?.name || row?.vendorRef?.name || '');

                            const availableMethods = getAvailablePaymentMethodsDropdown(
                              row,
                              response.Country,
                              Type,
                              () => {},
                              tRoot,
                            ).filter((payment) => payment.visible && payment.value !== 'new');

                            if (availableMethods.length !== 0) {
                              setShowSingleCollect(true);
                            } else {
                              setUpdateInfo(true);
                            }
                          }}
                        >
                          {Type === 'Receivable'
                            ? t('Table.Row.collectButton', { defaultValue: 'Collect' })
                            : t('Table.Row.payButton', { defaultValue: 'Pay' })}
                        </Button>
                      )}

                      {(data.PaymentDetails || []).some(
                        (payment) =>
                          Number(vp.sanitizeToNumeric(String(payment.TransactionAmount))) > 0 ||
                          Number(vp.sanitizeToNumeric(String(payment.CreditAmount))) > 0,
                      ) && (
                        <Button
                          id={`toggle-detail-${data.id || ''}`}
                          colorScheme="secondary"
                          size="small"
                          iconRight={expandedRowIds.includes(data.id) ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                          onClick={() => {
                            setExpandedRowIds((ids) =>
                              ids.includes(data.id) ? ids.filter((rowId) => rowId !== data.id) : [...ids, data.id],
                            );
                          }}
                        >
                          {expandedRowIds.includes(data.id)
                            ? t('Table.Row.hideDetailButton', { defaultValue: 'Hide Detail' })
                            : t('Table.Row.showDetailButton', { defaultValue: 'Show Detail' })}
                        </Button>
                      )}
                    </Box>
                  ),
              },
            ]}
            rows={tempBillsOrInvoices
              .map((item, i) => ({
                id: item.id || String(i),
                data: item,
                className: hasFailedPayment(item) ? 'negative2' : item.isSelected ? 'highlight' : '',
              }))
              .slice(offset, offset + PER_PAGE)}
            expandedRowIds={expandedRowIds}
            renderExpandedContent={({ data }) => (
              <NestedTransactionsTable
                DocumentID={data.id}
                Payments={data.PaymentDetails || []}
                Currency={data.currency || ''}
                ServiceName={ServiceName}
              />
            )}
          />
        </Box>

        <Box width="100%" jc="center" mt="lg" mb="sm">
          {pageCount > 1 && (
            <Pagination
              pageCount={pageCount}
              forcePageNumber={currentPage}
              handlePageClick={({ selected }) => setCurrentPage(selected)}
            />
          )}
        </Box>

        {actionsEnabled &&
          createPortal(
            <>
              <AccountingSystemSingleDialogue
                isVisible={showSingleCollect}
                close={() => {
                  setShowSingleCollect(false);
                }}
                type={Type}
                billsAndInvoices={updatingItem}
                financialInstitutions={financialInstitutionsResp?.FinancialInstitutions || {}}
                onCollect={(collectionAndPaymentItem, isUpdated) => {
                  if (isUpdated) {
                    onPayOrCollect([collectionAndPaymentItem], ToastVisibilityState.PaymentMethodSaved);
                  } else if (Number(updatingItem?.PaidAmount) !== Number(collectionAndPaymentItem.PaidAmount)) {
                    onPayOrCollect([collectionAndPaymentItem], ToastVisibilityState.PartialCollection);
                  } else if (collectionAndPaymentItem.scheduledDate && collectionAndPaymentItem.scheduledDate !== '') {
                    onPayOrCollect([collectionAndPaymentItem], ToastVisibilityState.CollectionScheduled);
                  } else {
                    onPayOrCollect([collectionAndPaymentItem], ToastVisibilityState.FinalCollectionRequestSent);
                  }

                  setShowSingleCollect(false);
                }}
              />

              <AccountingSystemScheduleDialogue
                isVisible={showEditSchedule}
                close={() => {
                  setShowEditSchedule(false);
                }}
                defaultDate={scheduleDate}
                onSchedule={(newDate) => {
                  if (updatingItem) {
                    updateScheduleDate(newDate, [updatingItem], ToastVisibilityState.ScheduleDateUpdate);
                    setShowEditSchedule(false);
                  }
                }}
              />

              <BulkPaymentDialogue
                isVisible={showBulkPayment}
                close={(selectedList, isSaved) => {
                  setShowBulkPayment(false);

                  if (isSaved) {
                    setShowCancel(true);
                    setConfirmedBillsOrInvoices(selectedList);
                  }
                }}
                onSchedule={(selectedList) => {
                  collectOrPayBulkSchedule(selectedList, ToastVisibilityState.MultipleRecipientsScheduled);
                  setShowBulkPayment(false);
                }}
                financialInstitutions={financialInstitutionsResp?.FinancialInstitutions || {}}
                listOfSelected={confirmedBillsOrInvoices}
                type={Type}
                onScheduleDate={(selectedList, newDate) => {
                  setShowScheduleCustomDate(true);
                  setConfirmedBillsOrInvoices(selectedList);
                  setConfirmedDate(newDate);
                  setShowBulkPayment(false);
                }}
                onCollect={(selectedList) => {
                  onPayOrCollect(selectedList, ToastVisibilityState.MultipleRecipientsRequestSent);
                  setShowBulkPayment(false);
                }}
              />

              <BankingUpdateDialogue
                isVisible={isUpdateInfo}
                close={() => {
                  setUpdateInfo(false);
                }}
                type={Type}
                originalValue={updatingItem}
                financialInstitutions={financialInstitutionsResp?.FinancialInstitutions || {}}
                onSave={(selected) => {
                  onPayOrCollect([selected], ToastVisibilityState.PaymentMethodSaved);
                  setUpdateInfo(false);
                }}
              />

              <AccountingSystemDialogue handleDialogue={handlers.handleAccountDialogue} />
            </>,
            document.body,
          )}
      </Box>
    </AccountingSystemContext.Provider>
  );

  function checkBalance(values: vp.TemporaryBillsOrInvoicesRails[]): boolean {
    const AvailableBalance = Number(AccountBalance?.AvailableFunds ?? 0);

    const totalAmount = values.reduce((accumulator, currentValue) => {
      if (currentValue.scheduledDate && currentValue.scheduledDate !== '') {
        return accumulator;
      }

      const amount = Number(currentValue.PaidAmount);

      return Number(accumulator) + amount;
    }, 0);

    if (AvailableBalance < totalAmount) {
      setShowInsufficientFunds(true);

      return false;
    }

    return true;
  }

  function formatDateDifference(date: Date): string {
    const now = new Date();
    const diffInDays = Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays < -1) {
      if (diffInDays < -30) {
        return t('DateHelper.monthsAgo', {
          defaultValue: '{{days}} month(s) ago',
          days: Math.abs(Math.floor(diffInDays / 30)),
        });
      }

      if (diffInDays < -7) {
        return t('DateHelper.weeksAgo', {
          defaultValue: '{{weeks}} week(s) ago',
          weeks: Math.floor(Math.abs(diffInDays) / 7),
        });
      }

      return t('DateHelper.daysAgo', { defaultValue: '{{days}} day(s) ago', days: Math.abs(diffInDays) });
    }

    if (diffInDays === -1) {
      return t('DateHelper.yesterday', { defaultValue: 'Yesterday' });
    }

    if (diffInDays === 0) {
      return t('DateHelper.today', { defaultValue: 'Today' });
    }

    if (diffInDays === 1) {
      return t('DateHelper.tomorrow', { defaultValue: 'Tomorrow' });
    }

    if (diffInDays <= 7) {
      return t('DateHelper.inDays', { defaultValue: 'in {{days}} day(s)', days: Math.abs(diffInDays) });
    }

    if (diffInDays < 30) {
      return t('DateHelper.inWeeks', { defaultValue: 'in {{weeks}} week(s)', weeks: Math.floor(diffInDays / 7) });
    }

    return t('DateHelper.inMonths', { defaultValue: 'in {{months}} month(s)', months: Math.floor(diffInDays / 30) });
  }

  function dateDiff(date: Date): number {
    const now = new Date();

    return Math.floor((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }

  function DateDisplay({ date }: { date: Date }): JSX.Element {
    const formattedDateDifference = formatDateDifference(date);
    const textColor = dateDiff(date) <= 7 ? '#D22D2D' : '#2d2d2db2';

    return (
      <div style={{ color: textColor }} className={styles.dateDiff}>
        {formattedDateDifference}
      </div>
    );
  }

  function formatDateOfObjects(listObject: vp.TemporaryBillsOrInvoicesRails[]): vp.TemporaryBillsOrInvoicesRails[] {
    const today = startOfDay(new Date()); // Get today's date, ignoring time

    return listObject.map((item) => {
      const dueDate = parseISO(item.dueDate);

      // If due date is greater than today's date, set scheduleStartDate as due date
      if (isAfter(startOfDay(dueDate), today)) {
        return { ...item, scheduledDate: format(dueDate, 'yyyy-MM-dd') };
      }

      return { ...item, scheduledDate: '' }; // Leave blank otherwise
    });
  }

  function enableToastVisibility(state: ToastVisibilityState) {
    switch (state) {
      case ToastVisibilityState.ReceivableWithPaymentMethods:
        setIsReceivableWithPaymentMethodsVisible(true);
        break;
      case ToastVisibilityState.ScheduledForCollection:
        setIsScheduledForCollectionVisible(true);
        break;
      case ToastVisibilityState.PaymentMethodSaved:
        setIsPaymentMethodSavedVisible(true);
        break;
      case ToastVisibilityState.CollectionRequestSent:
        setIsCollectionRequestSentVisible(true);
        break;
      case ToastVisibilityState.CollectionScheduled:
        setIsCollectionScheduledVisible(true);
        break;
      case ToastVisibilityState.MultipleRecipientsRequestSent:
        setIsMultipleRecipientsRequestSentVisible(true);
        break;
      case ToastVisibilityState.MultipleRecipientsScheduled:
        setIsMultipleRecipientsScheduledVisible(true);
        break;
      case ToastVisibilityState.PaymentMethodSavedToRecipients:
        setIsPaymentMethodSavedToRecipientsVisible(true);
        break;
      case ToastVisibilityState.PartialCollection:
        setIsPartialCollectionVisible(true);
        break;
      case ToastVisibilityState.FinalCollectionRequestSent:
        setIsFinalCollectionRequestSentVisible(true);
        break;
      case ToastVisibilityState.ScheduleDateUpdate:
        setShowSuccessScheduleDateUpdated(true);
        break;
      default:
        break;
    }
  }
};

export default AccountingSystemOpen;
